import { ValuePills } from '@/components/ui';
import {
  NO_CYCLE_LABEL,
  PURCHASE_HISTORY_HEADING,
  purchaseCycleLabel,
  purchaseHistoryOf,
} from '@/lib/company-purchases';
import type { Company } from '@/lib/types';

/**
 * Which projects a company took part in, one row per cycle. Read-only —
 * derived server-side from its issued invoices' lines.
 *
 * Projects only: the owner removed products purchased from the company page on
 * 2026-10-12. The `/companies` list and the UP dashboard cells still show both,
 * through `PurchaseHistoryCell`. A cycle whose invoices name no project is left
 * out rather than drawn as a row holding only a dash.
 *
 * ⛔ Rows render in the order the server sent them (newest cycle first, the
 * no-cycle bucket last). Never re-sort here.
 *
 * ⚠️ `null` (an older server, or the key not read) and `[]` (read, nothing
 * found) are different facts and render differently. `null` makes no claim
 * about the company; an empty list says no issued invoice names a project.
 *
 * Carries the same shell as `DetailGroup` on the page, written out because
 * `DetailGroup` wraps its children in a `<dl>` grid and these rows are not
 * label/value pairs.
 */
export function CompanyPurchaseHistory({ company }: { company: Company }) {
  const history = purchaseHistoryOf(company);
  const rows = history?.filter((row) => row.projects.length > 0) ?? null;

  return (
    <div className="border-b border-border px-4 py-4 last:border-b-0">
      <h3 className="m-0 text-sm font-semibold text-fg">{PURCHASE_HISTORY_HEADING}</h3>
      {rows === null ? (
        <p className="m-0 mt-3 text-sm text-fg-muted">Not available</p>
      ) : rows.length === 0 ? (
        <p className="m-0 mt-3 text-sm text-fg-muted">
          No issued invoice names a project for this company.
        </p>
      ) : (
        <>
          {/* Column headings once, from md up; below md each row labels itself. */}
          <div className="mt-3 hidden gap-x-6 md:grid md:grid-cols-[10rem_minmax(0,1fr)]">
            <span className="text-xs font-medium text-fg-muted">Cycle</span>
            <span className="text-xs font-medium text-fg-muted">Projects</span>
          </div>
          <ul className="m-0 mt-2 list-none divide-y divide-border p-0">
            {rows.map((row) => (
              <li
                key={row.cycle ? `cycle:${row.cycle.id}` : 'cycle:none'}
                className="grid grid-cols-1 gap-x-6 gap-y-2 py-2.5 md:grid-cols-[10rem_minmax(0,1fr)]"
              >
                <span
                  className={
                    row.cycle
                      ? 'min-w-0 text-sm font-medium text-fg [overflow-wrap:anywhere]'
                      : 'min-w-0 text-sm text-fg-muted [overflow-wrap:anywhere]'
                  }
                >
                  {row.cycle ? purchaseCycleLabel(row.cycle) : NO_CYCLE_LABEL}
                </span>
                <div className="min-w-0">
                  <span className="mb-1 block text-xs font-medium text-fg-muted md:hidden">
                    Projects
                  </span>
                  <ValuePills
                    items={row.projects.map((ref) => ({ key: ref.id, label: ref.name }))}
                  />
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
